import { forwardRef, useLayoutEffect, useRef, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";

const DEFAULT_LINE_HEIGHT = 20;
const COLLAPSED_MAX_HEIGHT = 360;
const EXPANDED_MIN_HEIGHT = 360;
const EXPANDED_VIEWPORT_RATIO = 0.6;

export type ExpandableTextareaProps = Omit<
  React.ComponentProps<typeof Textarea>,
  "value" | "rows"
> & {
  value: string;
  minRows?: number;
};

export const ExpandableTextarea = forwardRef<HTMLTextAreaElement, ExpandableTextareaProps>(
  function ExpandableTextarea({ value, minRows = 4, className, ...props }, forwardedRef) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const [expanded, setExpanded] = useState(false);

    function setRef(el: HTMLTextAreaElement | null) {
      innerRef.current = el;
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    }

    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;

      function minHeightFor(target: HTMLTextAreaElement) {
        const cs = getComputedStyle(target);
        const px = (raw: string) => parseFloat(raw) || 0;
        const lineHeight = parseFloat(cs.lineHeight) || DEFAULT_LINE_HEIGHT;
        const box =
          px(cs.paddingTop) +
          px(cs.paddingBottom) +
          px(cs.borderTopWidth) +
          px(cs.borderBottomWidth);
        return Math.ceil(lineHeight * minRows + box);
      }

      function resize() {
        if (!el) return;
        if (expanded) {
          el.style.height = `${Math.max(
            EXPANDED_MIN_HEIGHT,
            Math.round(window.innerHeight * EXPANDED_VIEWPORT_RATIO),
          )}px`;
          return;
        }
        el.style.height = "auto";
        const grown = Math.max(el.scrollHeight, minHeightFor(el));
        el.style.height = `${Math.min(grown, COLLAPSED_MAX_HEIGHT)}px`;
      }

      resize();
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }, [value, expanded, minRows]);

    const toggleLabel = expanded ? "Collapse editor" : "Expand editor";

    return (
      <div className="relative">
        <Textarea
          ref={setRef}
          value={value}
          rows={minRows}
          className={cn("resize-none overflow-y-auto pr-10", className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setExpanded((v) => !v)}
          aria-label={toggleLabel}
          title={toggleLabel}
          className="absolute right-1.5 top-1.5 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>
    );
  },
);
